import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/store/features/baseRtkQuery';

export type MessageTemplate = {
    id: string;
    templateId: string;
    displayName: string;
    definition: Record<string, unknown>;
    isActive: boolean;
    version: number;
    createdAt: string;
    updatedAt: string;
    /** BCP-47 tag the top-level body is written in. Derived server-side from the definition. */
    defaultLanguage?: string;
    /** defaultLanguage plus every translations key. Derived server-side; never stored. */
    availableLanguages?: string[];
};

export type CreateMessageTemplateRequest = {
    templateId: string;
    displayName: string;
    definition: Record<string, unknown>;
    isActive?: boolean;
};

export type UpdateMessageTemplateRequest = {
    displayName?: string;
    definition?: Record<string, unknown>;
    isActive?: boolean;
};

export const messageTemplatesApiSlice = createApi({
    reducerPath: 'messageTemplatesApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['MessageTemplates'],
    endpoints: (builder) => ({
        listMessageTemplates: builder.query<MessageTemplate[], { activeOnly?: boolean } | void>({
            query: (args) => ({
                url: 'api/channel-message-templates',
                method: 'GET',
                params: args?.activeOnly ? { activeOnly: true } : undefined,
            }),
            providesTags: [{ type: 'MessageTemplates', id: 'LIST' }],
        }),
        createMessageTemplate: builder.mutation<MessageTemplate, CreateMessageTemplateRequest>({
            query: (body) => ({
                url: 'api/channel-message-templates',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'MessageTemplates', id: 'LIST' }],
        }),
        updateMessageTemplate: builder.mutation<
            MessageTemplate,
            { id: string; body: UpdateMessageTemplateRequest }
        >({
            query: ({ id, body }) => ({
                url: `api/channel-message-templates/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: [{ type: 'MessageTemplates', id: 'LIST' }],
        }),
        deleteMessageTemplate: builder.mutation<{ success?: boolean } | void, string>({
            query: (id) => ({
                url: `api/channel-message-templates/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'MessageTemplates', id: 'LIST' }],
        }),
    }),
});

export const messageTemplatesApiActions = {
    useListMessageTemplatesQuery: messageTemplatesApiSlice.useListMessageTemplatesQuery,
    useCreateMessageTemplateMutation: messageTemplatesApiSlice.useCreateMessageTemplateMutation,
    useUpdateMessageTemplateMutation: messageTemplatesApiSlice.useUpdateMessageTemplateMutation,
    useDeleteMessageTemplateMutation: messageTemplatesApiSlice.useDeleteMessageTemplateMutation,
};
